import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { cleanupInspectionStorage } from '../utils/inspectionStorage';
import './InspectionsList.css';

const formatDate = (dateValue) => {
  if (!dateValue) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(dateValue)).replace(/ /g, '-');
};

function InspectionsList({ onLogout }) {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchInspections = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/inspections', {
        params: { search: searchTerm },
        headers: { Authorization: `Bearer ${token}` },
      });
      setInspections(response.data.inspections || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load inspections.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections]);

  const rangeLabel = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return {
      start: `${start.getMonth() + 1}/${start.getDate()}/${start.getFullYear()}`,
      end: `${end.getMonth() + 1}/${end.getDate()}/${end.getFullYear()}`,
    };
  }, []);

  const startNewInspection = () => {
    cleanupInspectionStorage({ removeAllDrafts: true });
    [
      'inspectionId',
      'inspectionNumber',
      'inspectionDate',
      'inspectionSummary',
      'inspectionClientId',
      'inspectionVehicleId',
      'inspectionPictures',
      'inspectionChecklist',
      'inspectionDamageCanvas',
      'inspectionDamageImage',
    ].forEach((key) => localStorage.removeItem(key));
    navigate('/inspection/new');
  };

  return (
    <div className="home-container app-shell-fixed">
      <Sidebar onLogout={onLogout} />
      <main className="inspections-page app-fixed-main app-scroll-layout">
        <header className="inspections-header app-page-header">
          <div className="inspections-title-group app-title-group">
            <button className="app-back-button" type="button" onClick={() => navigate('/')} aria-label="Back to home" />
            <div className="app-title-copy">
              <h1>Inspections</h1>
              <p>Vehicle inspections, pictures, and damage records.</p>
            </div>
          </div>
          <div className="inspections-header-actions app-header-actions">
            <button type="button" onClick={fetchInspections}>{loading ? 'Refreshing...' : 'Refresh'} ↻</button>
            <button className="new-inspection-button" type="button" onClick={startNewInspection}>
              <span aria-hidden="true">+</span>
              New Inspection
            </button>
          </div>
        </header>

        <section className="inspections-toolbar">
          <select defaultValue="last30" aria-label="Date range">
            <option value="last30">Last 30 Days</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <input type="text" value={rangeLabel.start} readOnly aria-label="Start date" />
          <input type="text" value={rangeLabel.end} readOnly aria-label="End date" />
          <label className="inspections-search">
            <span aria-hidden="true" />
            <input
              type="search"
              placeholder="Search by inspection number, client name, or vehicle details"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
        </section>

        <section className="inspections-table-card app-scroll-results">
          <div className="inspections-table-scroll app-grid-scroll">
            <table className="inspections-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Img.</th>
                  <th>Client</th>
                  <th>Vehicle</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {inspections.map((inspection) => {
                  const vehicle = [inspection.Year, inspection.Make, inspection.Model].filter(Boolean).join(' ');
                  return (
                    <tr key={inspection.InspectionId}>
                      <td>{inspection.InspectionNumber}</td>
                      <td>{formatDate(inspection.InspectionDate)}</td>
                      <td>{inspection.HasDamageImage || inspection.PictureCount ? '✓' : ''}</td>
                      <td>{inspection.ClientName || '-'}</td>
                      <td>{vehicle || inspection.CarNumber || '-'}</td>
                      <td>
                        <button type="button" onClick={() => navigate(`/inspection/new?id=${inspection.InspectionId}`)}>→</button>
                      </td>
                    </tr>
                  );
                })}
                {inspections.length === 0 && (
                  <tr>
                    <td colSpan="6" className="inspections-empty-row">
                      {error || 'No inspections found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="inspections-pager">
            <div>
              <button type="button" disabled>‹‹</button>
              <button type="button" disabled>‹</button>
              <button type="button" className="active">1</button>
              <button type="button" disabled>›</button>
              <button type="button" disabled>››</button>
              <select defaultValue="20">
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <strong>items per page</strong>
            </div>
            <span>{inspections.length ? `1 - ${inspections.length} of ${inspections.length} items` : '0 items'}</span>
          </div>
        </section>

      </main>
    </div>
  );
}

export default InspectionsList;
